import { Link, useNavigate } from 'react-router-dom';
import { useClients } from '../api/queries/clients';
import { useLookupLabel } from '../api/queries/lookups';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { formatPhoneDisplay } from '../utils/phone';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import './ClientsPage.css';

type ClientSortKey = 'name' | 'industry' | 'email' | 'phone' | 'status' | 'tags';

export function ClientsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isPending, error } = useClients();
  const statusLabel = useLookupLabel('client_status');

  const clients = data?.clients ?? [];
  const sort = useClientSort(clients, (c, key: ClientSortKey) => {
    switch (key) {
      case 'name': return c.name;
      case 'industry': return c.industry ?? '';
      case 'email': return c.email ?? '';
      case 'phone': {
        const primary = c.phones.find((p) => p.is_primary) ?? c.phones[0];
        return primary?.phone ?? '';
      }
      case 'status': return statusLabel(c.status);
      case 'tags': return c.tags.join(', ');
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  if (isPending) return <p className="clients-status">Loading clients…</p>;

  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view clients."
        : 'Failed to load clients. Try again in a moment.';
    return <p className="clients-status clients-error">{msg}</p>;
  }

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Clients</h2>
        <div className="clients-header-actions">
          <span className="clients-count">{clients.length} total</span>
          {can('client.write') && (
            <Link to="/clients/new" className="btn-cyan-primary">
              New client
            </Link>
          )}
        </div>
      </header>

      {clients.length === 0 ? (
        <div className="section-body">
          <div className="clients-empty">
            <p>No clients yet.</p>
            <p className="clients-empty-meta">
              Create one via <code>POST /api/clients</code>. Web form coming
              in a follow-up commit.
            </p>
          </div>
        </div>
      ) : (
        <div className="section-body section-body-flush">
          <TableScroll id="clients">
            <table className="clients-table">
              <thead>
                <tr>
                  <SortableTh column="name" label="Name / Company" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="industry" label="Industry" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="email" label="Email" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="phone" label="Phone" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="tags" label="Tags" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((c) => (
                  <tr
                    key={c.id}
                    className="clients-row"
                    onClick={() => navigate(`/clients/${c.id}`)}
                  >
                    <td>
                      <div className="clients-name">{c.name}</div>
                      {c.kind === 'company' && c.company_dba_name && c.company_name ? (
                        <div className="clients-contact">Legal: {c.company_name}</div>
                      ) : null}
                    </td>
                    <td>{c.industry ?? '—'}</td>
                    <td>
                      {c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{(() => {
                      // Primary phone for the list view. The API marks the
                      // first row as is_primary; fall back to the first if
                      // legacy rows haven't been flagged yet.
                      const primary = c.phones.find((p) => p.is_primary) ?? c.phones[0];
                      return primary ? formatPhoneDisplay(primary.phone) : '—';
                    })()}</td>
                    <td>
                      <span className={`status-pill status-${c.status}`}>
                        {statusLabel(c.status)}
                      </span>
                    </td>
                    <td>
                      {c.tags.length > 0 ? (
                        <div className="clients-tags">
                          {c.tags.map((t) => (
                            <span key={t} className="clients-tag">
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
